/**
 * Legacy URL registry — the single source of truth for retired URLs.
 *
 * Two consumers:
 *   1. src/components/feature/LegacyRedirectGate.tsx — in-app forwarding, works
 *      on ANY host (Netlify, the Readdy preview domain, localhost).
 *   2. netlify.toml — real HTTP 301 rules so crawlers get a permanent redirect
 *      instead of a 200 "soft 404" app shell.
 *
 * Rules for editing:
 *   - `from` is a pathname only, no domain, no trailing slash, lowercase.
 *   - `to` must point at a route that actually exists in src/router/config.tsx,
 *     or at a real section anchor on the homepage. A stale target turns a 301
 *     into a 404, which is worse than having no redirect at all.
 *   - Never chain: if you rename a route that is already a redirect target,
 *     repoint every entry that lands on it.
 *   - Keep netlify.toml in sync. It cannot import this file because it is read
 *     at the edge before any app code runs.
 *
 * Categories are grouped for readability, not for behaviour.
 */

export interface LegacyRedirect {
  /** Old pathname that is no longer served, e.g. "/pricing". */
  from: string;
  /** Live destination path, optionally with a #section anchor. */
  to: string;
  /** What this entry is for. Shown nowhere; documentation for humans. */
  note: string;
}

/** Path-level redirects (everything that is not a blog article). */
export const legacyPageRedirects: LegacyRedirect[] = [
  // --- Home aliases -------------------------------------------------------
  { from: '/home', to: '/', note: 'Old explicit homepage link' },
  { from: '/index.html', to: '/', note: 'Static-era entry point' },
  { from: '/index.php', to: '/', note: 'Static-era entry point' },
  { from: '/landing', to: '/', note: 'Pre-launch landing page' },

  // --- Homepage section deep links ---------------------------------------
  // These were never routes, but people link and bookmark them as if they were.
  { from: '/pricing', to: '/#pricing', note: 'Section promoted to its own URL' },
  { from: '/plans', to: '/#pricing', note: 'Alternate wording for the pricing section' },
  { from: '/features', to: '/#features', note: 'Section promoted to its own URL' },
  { from: '/how-it-works', to: '/#how-it-works', note: 'Section promoted to its own URL' },
  { from: '/coaching-styles', to: '/#coaching-styles', note: 'Section promoted to its own URL' },
  { from: '/app-preview', to: '/#app-preview', note: 'Section promoted to its own URL' },
  { from: '/app', to: '/#app-preview', note: 'Short form of the app preview' },

  // --- Knowledge Hub ------------------------------------------------------
  // The hub now has its own page, so every earlier location must land there.
  { from: '/knowledge-hub', to: '/knowledge', note: 'Pre-launch name of the hub page' },
  { from: '/faq', to: '/knowledge', note: 'The 40 Q&As are the FAQ' },
  { from: '/faqs', to: '/knowledge', note: 'The 40 Q&As are the FAQ' },
  { from: '/questions', to: '/knowledge', note: 'Question catalogue alias' },

  // --- Blog index aliases -------------------------------------------------
  { from: '/articles', to: '/blog', note: 'Blog index under an earlier label' },
  { from: '/insights', to: '/blog', note: 'Blog index under an earlier label' },
  { from: '/posts', to: '/blog', note: 'WordPress-era archive path' },
  { from: '/news', to: '/blog', note: 'News section folded into the blog' },

  // --- Conversion / form confirmations ------------------------------------
  // The contact form confirms inline, so these post-submit pages are dead.
  { from: '/thank-you', to: '/contact', note: 'Post-submit page replaced by inline confirmation' },
  { from: '/thanks', to: '/contact', note: 'Post-submit page replaced by inline confirmation' },
  { from: '/thankyou', to: '/contact', note: 'Post-submit page replaced by inline confirmation' },
  { from: '/confirmation', to: '/contact', note: 'Post-submit page replaced by inline confirmation' },
  { from: '/success', to: '/contact', note: 'Post-submit page replaced by inline confirmation' },
  { from: '/message-sent', to: '/contact', note: 'Post-submit page replaced by inline confirmation' },

  // --- Email preference paths --------------------------------------------
  { from: '/opt-out', to: '/unsubscribe', note: 'Alternate unsubscribe wording' },
  { from: '/unsubscribe-confirmed', to: '/unsubscribe', note: 'Old confirm step, merged into one page' },

  // --- Legal paper trail --------------------------------------------------
  { from: '/legal/terms', to: '/terms', note: 'Nested legal path' },
  { from: '/legal/privacy', to: '/privacy', note: 'Nested legal path' },
  { from: '/legal/disclaimer', to: '/disclaimer', note: 'Nested legal path' },
  { from: '/cookie-policy', to: '/privacy', note: 'Cookie policy lives inside the privacy page' },

  // --- Campaign / lead-gen destinations -----------------------------------
  // SEEDS — these are the conventional slugs used in ads, emails and bios.
  // Verify each one against your actual campaign history and delete anything
  // you never published. A redirect for a URL that never existed is harmless
  // but it is noise.
  { from: '/free-guide', to: '/knowledge', note: 'Lead magnet pointed at the answer library' },
  { from: '/guide', to: '/knowledge', note: 'Lead magnet pointed at the answer library' },
  { from: '/ebook', to: '/knowledge', note: 'Lead magnet pointed at the answer library' },
  { from: '/challenge', to: '/#pricing', note: 'Challenge funnel entry' },
  { from: '/start', to: '/#pricing', note: 'Funnel entry point' },
  { from: '/get-started', to: '/#pricing', note: 'Funnel entry point' },
  { from: '/join', to: '/#pricing', note: 'Funnel entry point' },
  { from: '/signup', to: '/#pricing', note: 'Funnel entry point' },
  { from: '/trial', to: '/#pricing', note: 'Funnel entry point' },
  { from: '/webinar', to: '/contact', note: 'Event signup now runs through the contact form' },
  { from: '/masterclass', to: '/contact', note: 'Event signup now runs through the contact form' },
  { from: '/book-a-call', to: '/contact', note: 'Call booking now runs through the contact form' },
  { from: '/consultation', to: '/contact', note: 'Call booking now runs through the contact form' },
  { from: '/coaching', to: '/#coaching-styles', note: 'Coaching overview moved into the homepage' },
];

/**
 * Retired / renamed blog article slugs.
 *
 * Key   = old slug, without the "/blog/" prefix
 * Value = slug of the live article that should receive the visitor
 *
 * Blog paths are kept in a separate map because they are matched by the
 * article route itself (src/pages/Blog/components/BlogPostPage.tsx), not by
 * the path-level gate.
 */
export const retiredBlogSlugs: Record<string, string> = {
  // How to Stop Overthinking and Start Doing
  'stop-overthinking': 'how-to-stop-overthinking-and-start-doing',
  'how-to-stop-overthinking': 'how-to-stop-overthinking-and-start-doing',
  'stop-overthinking-and-start-doing': 'how-to-stop-overthinking-and-start-doing',

  // Building Daily Discipline That Actually Lasts
  'building-daily-discipline': 'building-daily-discipline-that-actually-lasts',
  'daily-discipline': 'building-daily-discipline-that-actually-lasts',
  'discipline-that-lasts': 'building-daily-discipline-that-actually-lasts',

  // The Confidence You Need Already Exists Inside You
  'confidence-already-exists': 'the-confidence-you-need-already-exists',
  'the-confidence-you-need': 'the-confidence-you-need-already-exists',
  'confidence-you-need-already-exists': 'the-confidence-you-need-already-exists',

  // Why Your Goals Aren't Working — And How to Fix Them
  'why-your-goals-arent-working': 'why-your-goals-arent-working-and-how-to-fix-them',
  'why-your-goals-are-not-working': 'why-your-goals-arent-working-and-how-to-fix-them',
  'why-your-goals-are-not-working-and-how-to-fix-them': 'why-your-goals-arent-working-and-how-to-fix-them',
  'fixing-your-goals': 'why-your-goals-arent-working-and-how-to-fix-them',

  // How to Make Better Decisions Under Pressure
  'better-decisions-under-pressure': 'how-to-make-better-decisions-under-pressure',
  'decisions-under-pressure': 'how-to-make-better-decisions-under-pressure',
  'how-to-make-better-decisions': 'how-to-make-better-decisions-under-pressure',

  // From Procrastination to Momentum in 10 Minutes
  'procrastination-to-momentum': 'from-procrastination-to-momentum-in-10-minutes',
  'from-procrastination-to-momentum': 'from-procrastination-to-momentum-in-10-minutes',
  'from-procrastination-to-momentum-in-ten-minutes': 'from-procrastination-to-momentum-in-10-minutes',
};